import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CountrySelect, PhoneInput, isValidPhone } from "@/components/FormControls";
import COUNTRIES from "@/lib/countries.json";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { GoogleWebSignIn } from "@/components/GoogleWebSignIn";

export default function Register() {
  const [params] = useSearchParams();
  const [role, setRole] = useState(params.get("role") === "agency" ? "agency" : "candidate");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: { code: "", number: "" }, country: "", agency_name: "" });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const nav = useNavigate();
  const done = (u) => {
    toast.success("Account ready!");
    if (u.role === "agency") { toast.info("New agencies stay pending until Emira approves them."); nav("/app/agency"); }
    else nav("/app/candidate");
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setCountry = (name) => {
    const c = COUNTRIES.find((x) => x.name === name);
    setForm({ ...form, country: name, phone: form.phone.number || !c ? form.phone : { ...form.phone, code: c.dial, iso: c.code } });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!isValidPhone(form.phone.number)) return toast.error("Enter a valid phone number (6 to 14 digits)");
    setLoading(true);
    try {
      const { phone: { iso, ...phone }, ...rest } = form;
      const u = await register({ ...rest, mobile: phone.number || null, mobile_code: phone.code || null, country: form.country || null, role });
      toast.success("Account created!");
      if (role === "agency") {
        toast.info("Your agency is pending Emira approval.");
        nav("/app/agency");
      } else {
        nav("/app/candidate");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-6 bg-background order-2 lg:order-1">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8"><Link to="/"><Logo variant="image" /></Link></div>
          <h1 className="font-display text-3xl font-bold text-[#0A192F]">Create your account</h1>
          <p className="text-slate-500 mt-1">Join Emira as a candidate or recruiting agency.</p>

          <div className="grid grid-cols-2 gap-2 mt-6 p-1 bg-slate-100 rounded-xl">
            {["candidate", "agency"].map((r) => (
              <button key={r} type="button" data-testid={`role-${r}`} onClick={() => setRole(r)}
                className={`py-2.5 rounded-lg text-sm font-medium capitalize transition-colors ${role === r ? "bg-white text-[#0088FF] shadow-sm" : "text-slate-500"}`}>
                {r}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="register-form">
            <div><Label>{role === "agency" ? "Contact Person Name" : "Full Name"} *</Label><Input data-testid="reg-name" required value={form.name} onChange={set("name")} /></div>
            {role === "agency" && <div><Label>Agency Name *</Label><Input data-testid="reg-agency-name" required value={form.agency_name} onChange={set("agency_name")} /></div>}
            <div><Label>Country</Label><CountrySelect testId="reg-country" value={form.country} onChange={setCountry} /></div>
            <div><Label>Mobile</Label><PhoneInput testId="reg-mobile" value={form.phone} onChange={(phone) => setForm({ ...form, phone })} /></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Email *</Label><Input data-testid="reg-email" type="email" required value={form.email} onChange={set("email")} /></div>
              <div><Label>Password *</Label><Input data-testid="reg-password" type="password" required minLength={6} value={form.password} onChange={set("password")} /></div>
            </div>
            <Button type="submit" disabled={loading} data-testid="register-submit" className="w-full bg-[#0088FF] hover:bg-[#0070E0] h-11">
              {loading ? "Creating..." : `Register as ${role}`}
            </Button>
          </form>
          <GoogleWebSignIn key={role} role={role} text="signup_with" onSignedIn={done} />
          <p className="text-xs text-slate-400 mt-2 text-center">Google sign-up creates {role === "agency" ? "an agency" : "a candidate"} account with your Google name and email.</p>
          <p className="text-sm text-slate-500 mt-5">Already have an account? <Link to="/login" className="text-[#0088FF] font-medium">Sign in</Link></p>
        </div>
      </div>

      <div className="hidden lg:flex brand-gradient-navy relative overflow-hidden flex-col justify-between p-12 order-1 lg:order-2">
        <div className="absolute inset-0 brand-dotgrid opacity-40" />
        <Link to="/" className="relative self-end"><Logo variant="light" showTagline /></Link>
        <div className="relative">
          <h2 className="font-display text-4xl font-extrabold text-white leading-tight">Your global career<br />starts here.</h2>
          <p className="text-slate-300 mt-4 max-w-md">Build your profile once, apply to international jobs, upload documents and track every stage to deployment.</p>
        </div>
        <div className="relative text-xs text-slate-400">© {new Date().getFullYear()} Emira Manpower &amp; Training Solutions</div>
      </div>
    </div>
  );
}
